import { styled } from '@openeducation/oe-it-themes';
import { MotiView } from 'moti';
import React from 'react';
import { useWindowDimensions } from 'react-native';

import { PopupPosition } from './popup.types';
import { ExtendedLayoutRectangle } from './use-popup';
import { maxWidth } from '../../style-utils';

type PopupContainerProps = {
  childrenRect?: ExtendedLayoutRectangle;
  finalPopupPosition: PopupPosition;
  isVisible: boolean;
  popupSpacing: number;
  children: React.ReactNode;
};

export function PopupContainer({
  childrenRect,
  finalPopupPosition,
  isVisible,
  popupSpacing,
  children,
}: PopupContainerProps) {
  const { width } = useWindowDimensions();

  const { hiddenStyles, visibleStyles } = React.useMemo(() => {
    const multiplier = finalPopupPosition.includes('top') ? -1 : 1;
    const isLeft = finalPopupPosition.includes('left');
    const isRight = finalPopupPosition.includes('right');
    const horizontalSpacing = 16;

    const hidden = {
      opacity: 0,
      scale: 0.8,
      translateX: isLeft ? horizontalSpacing : isRight ? -horizontalSpacing : 0,
      translateY: 2 * popupSpacing * -multiplier,
    };

    const visible = {
      opacity: 1,
      scale: 1,
      translateX: 0,
      translateY: popupSpacing * multiplier,
    };

    return { hiddenStyles: hidden, visibleStyles: visible };
  }, [finalPopupPosition, popupSpacing]);

  return (
    <Container
      top={childrenRect?.pageY}
      left={childrenRect?.pageX}
      width={childrenRect?.width}
      height={childrenRect?.height}
      popupPosition={finalPopupPosition}
      pointerEvents={isVisible ? 'auto' : 'none'}
    >
      <InnerContainer
        popupPosition={finalPopupPosition}
        from={hiddenStyles}
        animate={visibleStyles}
        exit={hiddenStyles}
        style={maxWidth(width)}
      >
        {children}
      </InnerContainer>
    </Container>
  );
}

interface ContainerProps {
  top?: number;
  left?: number;
  width?: number;
  height?: number;
  popupPosition: PopupPosition;
}

const Container = styled.View<ContainerProps>`
  position: absolute;
  flex-direction: row;
  justify-content: ${({ popupPosition }) => {
    if (popupPosition.includes('left')) {
      return 'flex-end';
    }
    if (popupPosition.includes('right')) {
      return 'flex-start';
    }
    return 'center';
  }};
  top: ${({ top = 0 }) => top}px;
  left: ${({ left = 0 }) => left}px;
  width: ${({ width = 0 }) => width}px;
  margin-top: ${({ popupPosition, height = 0 }) =>
    popupPosition.includes('bottom') ? height : 0}px;
  height: 1px;
  background-color: transparent;
`;

interface InnerContainerProps {
  popupPosition: PopupPosition;
}

const InnerContainer = styled(MotiView)<InnerContainerProps>`
  position: absolute;
  align-items: ${({ popupPosition }) => {
    if (popupPosition.includes('left')) {
      return 'flex-end';
    }
    if (popupPosition.includes('right')) {
      return 'flex-start';
    }
    return 'center';
  }};
  z-index: 10;
  ${({ popupPosition }) => {
    if (popupPosition.includes('top')) {
      return 'bottom: 0';
    }
    return 'top: 0';
  }};
`;
